import { useMemo } from "react";

import { useSelectionStore } from "../../state/useSelectionStore";
import { useActiveScenario } from "../../state/useScenarioStore";
import { useFinanceStore } from "../../state/useFinanceStore";
import { useAreaScenario } from "../../lib/useScenario";
import { taxingCode, usePropertyTax } from "../../lib/usePropertyTax";
import { computePurchase, dutyRate } from "../../lib/finance";
import {
  AS_OF,
  MAX_DEBT_RATIO,
  MAX_TERM_YEARS,
  INSURANCE_BANK_GROUP,
  INSURANCE_DELEGATED,
  marketRate,
} from "../../lib/financeDefaults";
import { euro, euroShort, pricePerM2 } from "../../lib/format";
import { ScenarioPicker } from "../ScenarioPicker";
import { Choice, Figure, NumberField, Panel, Section } from "../ui/Field";
import {
  FeeBreakdown,
  MonthlyBreakdown,
  RunningCostBreakdown,
} from "./Breakdown";
import { AmortisationChart } from "./AmortisationChart";

const pctText = (v: number) => `${(v * 100).toFixed(2)}%`;

/**
 * What owning it actually costs, monthly.
 *
 * The headline most people carry around is the mensualité, which is the one
 * number that is reliably too small: it leaves out the borrower insurance the
 * bank requires anyway, the taxe foncière, and the service charges — together
 * often a fifth of the real burden. Everything here is on top of the loan.
 */
export function CostPanel({ visible }: { visible: boolean }) {
  const selection = useSelectionStore((s) => s.selection);
  const scenario = useActiveScenario();
  const f = useFinanceStore();

  const area = useMemo(
    () =>
      selection
        ? {
            scope: selection.scope,
            code: selection.code,
            name: selection.name,
            lon: selection.point.lon,
            lat: selection.point.lat,
          }
        : null,
    [selection],
  );
  const { projection } = useAreaScenario(area, scenario);
  const { data: tax } = usePropertyTax(taxingCode(selection));

  // The commune's own figure when we have one, otherwise the regional default
  // the user can still edit.
  const taxPerM2 =
    f.propertyTaxMode === "commune" && tax ? tax.tax_per_m2 : f.propertyTaxPerM2;

  const estimated = projection?.base.value ?? null;
  const price = f.priceMode === "manual" || estimated === null ? f.manualPrice : estimated;
  const surface = scenario.surface;
  const rate = f.rateMode === "market" ? marketRate(f.years) : f.manualRate;

  const purchase = useMemo(
    () =>
      computePurchase({
        price,
        agencyFees: f.agencyFees,
        works: f.works,
        kind: f.kind,
        firstTimeBuyer: f.firstTimeBuyer,
        deposit: f.deposit,
        years: f.years,
        rate,
        insuranceRate: f.insuranceRate,
        insuranceBasis: f.insuranceBasis,
        quotity: f.quotity,
        arrangementFee: f.arrangementFee,
        guaranteeRate: f.guaranteeRate,
        brokerFee: f.brokerFee,
        propertyTax: taxPerM2 * surface,
        serviceCharges: f.serviceChargePerM2 * surface,
        homeInsurance: f.homeInsurance,
        income: f.income,
      }),
    [price, surface, rate, taxPerM2, f],
  );

  const overBorrowed = purchase.debtRatio !== null && purchase.debtRatio > MAX_DEBT_RATIO;

  return (
    <div className="px-4 py-3">
      <div className="rounded-lg border border-line px-3 py-2.5">
        <div className="text-[10px] font-medium uppercase tracking-wider text-ink-soft">
          Every month
        </div>
        <div className="mt-0.5 text-2xl font-semibold tabular-nums">
          {euro(purchase.monthly.total)}
        </div>
        <div className="mt-2">
          <MonthlyBreakdown purchase={purchase} />
        </div>
      </div>

      <div className="mt-2 grid grid-cols-3 gap-2">
        <Figure label="Cash to sign" value={euroShort(purchase.cashNeeded)} />
        <Figure label="Borrowed" value={euroShort(purchase.loanAmount)} />
        <Figure
          label="TAEG"
          value={purchase.taeg === null ? "—" : pctText(purchase.taeg)}
          hint={`All-in annual rate: the nominal ${pctText(rate)} plus insurance and fees.`}
        />
      </div>

      {purchase.debtRatio !== null && (
        <p
          className={`mt-2 rounded-lg px-2.5 py-1.5 text-[11px] leading-snug ${
            overBorrowed
              ? "bg-[#cf3a2e]/10 text-[#cf3a2e]"
              : "bg-surface-alt text-ink-soft"
          }`}
        >
          <span className="font-semibold tabular-nums">
            {(purchase.debtRatio * 100).toFixed(1)}%
          </span>{" "}
          of income to the bank
          {overBorrowed
            ? ` — over the ${MAX_DEBT_RATIO * 100}% banks lend to.`
            : ` (limit ${MAX_DEBT_RATIO * 100}%).`}{" "}
          All in, {((purchase.burdenRatio ?? 0) * 100).toFixed(1)}%.
        </p>
      )}

      <Panel title="What you are buying" summary={euroShort(price)}>
        <ScenarioPicker />
        <Choice
          label="Price"
          value={f.priceMode}
          onChange={(v) => f.set("priceMode", v)}
          options={[
            { value: "estimate", label: `${surface} m² here` },
            { value: "manual", label: "My own" },
          ]}
        />
        {f.priceMode === "manual" || estimated === null ? (
          <NumberField
            label="Purchase price"
            value={f.manualPrice}
            onChange={(v) => f.set("manualPrice", v)}
            step={5000}
            suffix="€"
          />
        ) : (
          <p className="text-[11px] text-ink-soft">
            <span className="font-medium text-ink tabular-nums">{euro(price)}</span> —{" "}
            {surface} m² in {area?.name} at {pricePerM2(projection!.base.perM2)}
          </p>
        )}

        <div className="grid grid-cols-2 gap-2">
          <NumberField
            label="Deposit"
            value={f.deposit}
            onChange={(v) => f.set("deposit", v)}
            step={5000}
            suffix="€"
            hint="Apport personnel. Most banks want it to cover at least the notary fees."
          />
          <NumberField
            label="Term"
            value={f.years}
            onChange={(v) => f.set("years", Math.max(1, Math.min(30, v)))}
            step={1}
            min={1}
            suffix="yr"
            hint={`Lending rules cap most loans at ${MAX_TERM_YEARS} years.`}
          />
        </div>

        <div>
          <Choice
            label="Interest rate"
            value={f.rateMode}
            onChange={(v) => f.set("rateMode", v)}
            options={[
              { value: "market", label: `Market (${pctText(marketRate(f.years))})` },
              { value: "manual", label: "My offer" },
            ]}
          />
          {f.rateMode === "manual" && (
            <div className="mt-2">
              <NumberField
                label="Nominal rate"
                value={Number((f.manualRate * 100).toFixed(2))}
                onChange={(v) => f.set("manualRate", v / 100)}
                step={0.05}
                suffix="%"
              />
            </div>
          )}
        </div>
      </Panel>

      <Section
        title="Loan insurance"
        summary={`${pctText(f.insuranceRate * f.quotity)} · ${euro(purchase.monthly.insurance)}/mo`}
      >
        <Choice
          label="Contract"
          value={f.insuranceRate === INSURANCE_DELEGATED ? "delegated" : "bank"}
          onChange={(v) =>
            f.set(
              "insuranceRate",
              v === "delegated" ? INSURANCE_DELEGATED : INSURANCE_BANK_GROUP,
            )
          }
          options={[
            { value: "bank", label: `Bank (${pctText(INSURANCE_BANK_GROUP)})` },
            { value: "delegated", label: `Outside (${pctText(INSURANCE_DELEGATED)})` },
          ]}
        />
        <Choice
          label="Charged on"
          value={f.insuranceBasis}
          onChange={(v) => f.set("insuranceBasis", v)}
          options={[
            { value: "initial", label: "Initial capital" },
            { value: "remaining", label: "What's still owed" },
          ]}
        />
        <div className="grid grid-cols-2 gap-2">
          <NumberField
            label="Rate"
            value={Number((f.insuranceRate * 100).toFixed(3))}
            onChange={(v) => f.set("insuranceRate", v / 100)}
            step={0.01}
            suffix="%"
          />
          <NumberField
            label="Borrowers"
            value={f.quotity}
            onChange={(v) => f.set("quotity", Math.max(1, Math.min(2, v)))}
            step={1}
            min={1}
            hint="Two borrowers each covered at 100% doubles the premium."
          />
        </div>
        <p className="text-[10px] leading-snug text-ink-soft">
          Over this loan: {euro(purchase.loan.totalInsurance)}.
        </p>
      </Section>

      <Section
        title="Fees"
        summary={euroShort(
          purchase.notary.total +
            purchase.arrangementFee +
            purchase.guaranteeFee +
            purchase.brokerFee +
            f.agencyFees,
        )}
      >
        <FeeBreakdown purchase={purchase} agencyFees={f.agencyFees} />
        <p className="text-[10px] leading-snug text-ink-soft">
          Duties are {pctText(dutyRate(f.kind, f.firstTimeBuyer))} of the price;
          notary total {euro(purchase.notary.total)}, or{" "}
          {pctText(purchase.notary.rate)}.
        </p>
        <Choice
          label="Property"
          value={f.kind}
          onChange={(v) => f.set("kind", v)}
          options={[
            { value: "ancien", label: "Existing" },
            { value: "neuf", label: "New build" },
          ]}
        />
        <label className="flex items-center gap-2 text-[11px]">
          <input
            type="checkbox"
            checked={f.firstTimeBuyer}
            onChange={(e) => f.set("firstTimeBuyer", e.target.checked)}
            className="h-3.5 w-3.5 accent-accent"
          />
          First-time buyer, main residence
        </label>
        <div className="grid grid-cols-2 gap-2">
          <NumberField
            label="Agency fee"
            value={f.agencyFees}
            onChange={(v) => f.set("agencyFees", v)}
            suffix="€"
            hint="Only if you pay it separately — often it is already inside the asking price."
          />
          <NumberField
            label="Renovation"
            value={f.works}
            onChange={(v) => f.set("works", v)}
            suffix="€"
          />
        </div>
      </Section>

      <Section
        title="Running costs"
        summary={`${euro(purchase.monthly.propertyTax + purchase.monthly.serviceCharges + purchase.monthly.homeInsurance)}/mo`}
      >
        <RunningCostBreakdown purchase={purchase} />
        <Choice
          label="Taxe foncière"
          value={f.propertyTaxMode}
          onChange={(v) => f.set("propertyTaxMode", v)}
          options={[
            { value: "commune", label: tax ? `${tax.name} rate` : "By commune" },
            { value: "manual", label: "From the notice" },
          ]}
        />
        {f.propertyTaxMode === "commune" && tax ? (
          <p className="text-[10px] leading-snug text-ink-soft">
            {tax.name} votes{" "}
            <span className="font-medium text-ink tabular-nums">
              {(tax.rate * 100).toFixed(2)}%
            </span>{" "}
            {tax.exact ? "" : "on average "}({tax.source_year}), on an estimated
            base of {Math.round(tax.base_per_m2)} €/m² — about{" "}
            <span className="font-medium text-ink tabular-nums">
              {euro(taxPerM2 * surface)}
            </span>{" "}
            a year at {surface} m². The rate is exact; the base is modelled, so
            put in the seller's own figure when you have it.
          </p>
        ) : (
          <NumberField
            label="Taxe foncière"
            value={f.propertyTaxPerM2}
            onChange={(v) => f.set("propertyTaxPerM2", v)}
            step={1}
            suffix="€/m²/yr"
            hint="The seller's tax notice has the real figure."
          />
        )}
        <NumberField
          label="Charges"
          value={f.serviceChargePerM2}
          onChange={(v) => f.set("serviceChargePerM2", v)}
          step={5}
          suffix="€/m²/yr"
          hint="Copropriété charges — higher with a lift, a concierge or shared heating."
        />
        <NumberField
          label="Home insurance"
          value={f.homeInsurance}
          onChange={(v) => f.set("homeInsurance", v)}
          step={20}
          suffix="€/yr"
        />
      </Section>

      <Section
        title="Affordability"
        summary={f.income > 0 ? `${euro(f.income)}/mo net` : "not set"}
      >
        <NumberField
          label="Net monthly income"
          value={f.income}
          onChange={(v) => f.set("income", v)}
          step={100}
          suffix="€"
          hint="Household take-home pay. Leave at 0 to hide the ratio."
        />
      </Section>

      <Panel
        title={`Over ${f.years} years`}
        summary={euroShort(purchase.totalPaid)}
      >
        {visible && <AmortisationChart loan={purchase.loan} />}
        <ul className="space-y-0.5">
          <FeeRow label="Interest" value={purchase.loan.totalInterest} />
          <FeeRow label="Loan insurance" value={purchase.loan.totalInsurance} />
          <FeeRow label="Paid in total" value={purchase.totalPaid} strong />
        </ul>
      </Panel>

      <p className="mt-3 text-[10px] leading-snug text-ink-soft">
        Rates and duties as of {AS_OF}; every figure above is editable.
      </p>
    </div>
  );
}


function FeeRow({
  label,
  value,
  strong,
}: {
  label: string;
  value: number;
  strong?: boolean;
}) {
  return (
    <li
      className={`flex items-baseline gap-2 text-[11px] ${
        strong ? "border-t border-line pt-1 font-semibold" : ""
      }`}
    >
      <span className="flex-1 truncate text-ink-soft">{label}</span>
      <span className="tabular-nums">{euro(value)}</span>
    </li>
  );
}
